import React, { Key, useState } from "react";

import { useHistory } from "react-router-dom";
import styled from "styled-components";

import { useGetVero1Checklist } from "manager/hooks/api/vero1Queries";
import { Vero1ChecklistStatus } from "shared/interfaces/api/vero1";

import { InternalOnlyChip } from "./InternalOnlyChip";
import { openDealReview } from "./openDealReview";
import { useVero1Viewer } from "./vero1Viewer";

const Band = styled.div`
  /* canvas 4.4: gold-on-dark, flush above the verdict card */
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 26px;
  background: #3a3350;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  font-size: 13px;
`;

const RunDot = styled.span`
  flex: 0 0 auto;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #dcc49c;
`;

const Message = styled.span`
  flex: 1 1 auto;
  font-weight: 500;
  color: #ede6d6;
`;

/* drawn 4.4: gold link, 500 12px, pushed right */
const OpenReview = styled.button`
  flex: 0 0 auto;
  margin-left: auto;
  font: 500 12px "Work Sans", sans-serif;
  color: #dcc49c;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  white-space: nowrap;
`;

const Dismiss = styled.button`
  flex: 0 0 auto;
  width: 22px;
  height: 22px;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  color: #a99f87;

  &:hover {
    color: #ede6d6;
  }
`;

const dismissKey = (dealId: Key) => `vero1-rereview-dismissed:${dealId}`;

const readDismissed = (dealId: Key) => {
  try {
    return window.localStorage.getItem(dismissKey(dealId));
  } catch {
    return null;
  }
};

const storeDismissed = (dealId: Key, runId: string) => {
  try {
    window.localStorage.setItem(dismissKey(dealId), runId);
  } catch {
    // private mode: dismissal just won't survive a reload
  }
};

export interface Vero1RereviewBandProps {
  dealId: Key;
  vero1Active?: boolean;
}

/** The re-screen message, shared with the hold band. */
export const rereviewMessage = (rereview: {
  runAt?: string | number | null;
  changedCount?: number | null;
}): string => {
  const when = rereview.runAt
    ? ` on ${new Date(rereview.runAt).toLocaleDateString([], {
        month: "short",
        day: "numeric",
      })}`
    : "";
  // the backend suppresses zero-change and own-restart notices, so a null
  // count means the fresh facts could not be verified against this review
  const delta =
    rereview.changedCount && rereview.changedCount > 0
      ? `${rereview.changedCount} item${
          rereview.changedCount === 1 ? " differs" : "s differ"
        } from this review`
      : "its results couldn't be checked against this review";
  return `Lumen screened again${when} — ${delta}`;
};

/**
 * Deal-banner notice that a Lumen run landed after the submitted review
 * (spec 4.4): internal-only, touching nothing below it. Dismissal is stored
 * per run, so the band returns when the next run lands.
 */
export const Vero1RereviewBand = ({
  dealId,
  vero1Active,
}: Vero1RereviewBandProps) => {
  const history = useHistory();
  const { canViewInternal } = useVero1Viewer();
  const { checklist, rereview } = useGetVero1Checklist(dealId, vero1Active);
  const [dismissedRun, setDismissedRun] = useState<string | null>(() =>
    readDismissed(dealId)
  );

  if (!vero1Active || !canViewInternal) return null;
  const tripwire =
    checklist?.status === Vero1ChecklistStatus.Submitted && Boolean(rereview);
  if (!tripwire || !rereview) return null;
  if (dismissedRun === rereview.runId) return null;

  return (
    <Band data-testid="vero1-rereview-band">
      <RunDot aria-hidden />
      <Message>{rereviewMessage(rereview)}</Message>
      <InternalOnlyChip $tone="gold">Internal only</InternalOnlyChip>
      <OpenReview
        type="button"
        data-testid="open-deal-review"
        onClick={() => openDealReview(history, dealId)}
      >
        Open Deal review →
      </OpenReview>
      <Dismiss
        aria-label="Dismiss"
        onClick={() => {
          storeDismissed(dealId, rereview.runId);
          setDismissedRun(rereview.runId);
        }}
      >
        ×
      </Dismiss>
    </Band>
  );
};
